import './images.scss'
import { Component, For, JSX, Show, createEffect, createMemo, on, onMount } from 'solid-js'
import Modal from '../../shared/Modal'
import {
  ConfirmAction,
  hydrateMessageImages,
  ImageButton,
  imageStore,
  msgStore,
  pageStore,
  promptStore,
  toastStore,
} from '../../store'
import { getAssetUrl, getJsonSchema } from '../../shared/util'
import Button from '/web/shared/Button'
import { useDraft, useImageCache } from '/web/shared/hooks'
import TextInput from '/web/shared/TextInput'
import { ArrowLeft, ArrowRight, Download, SettingsIcon, Trash, WandSparkles } from 'lucide-solid'
import { cleanPrompt, hydrateTemplate } from '/common/util'
import { RelativeSpinner } from '/web/shared/Loading'
import { imageApi } from '/web/store/data/image'
import { getStore } from '/web/store/create'
import { createStore, SetStoreFunction } from 'solid-js/store'
import { ChatImageSettings, useCurrentChatImageSettings } from '../Settings/Image/ImageSettings'
import { Copy } from '/web/shared/Copy'
import { downloadImage } from '../Character/util'
import { debug } from '/common/debug'
import { genApi } from '/web/store/data/inference'
import { getImagePromptEntities } from '/web/store/data/common'
import { usePresetContext } from '/web/store/preset-context'
import { useAppContext } from '/web/store/context'
import { useImageContext } from '../Settings/Image/image-context'
import { SelectImageProvider } from '../Settings/Image/ImageProvider'

const log = debug('image-modal')

type ImageState = { prompt: string; promptLoading: boolean; loading: boolean }

export const ImageModal: Component = () => {
  const state = imageStore((s) => ({ showImage: s.showImage }))

  return (
    <>
      <Show
        when={state.showImage?.src.type === 'collection' || state.showImage?.src.type === 'message'}
      >
        <ImageCollectionModal />
      </Show>

      <Show when={state.showImage?.src.type === 'url'}>
        <ImageUrlModal
          url={state.showImage?.src.type === 'url' ? state.showImage?.src.id! : ''}
          close={() => imageStore.clearImage()}
          actions={state.showImage?.options!}
          onClose={state.showImage?.onClose}
        />
      </Show>
    </>
  )
}

const ImageUrlModal: Component<{
  url: string
  close: () => void
  actions: ImageButton[]
  onClose?: () => void
}> = (props) => {
  const footer = createMemo(() => {
    return (
      <div class="flex gap-2">
        <For each={props.actions}>
          {(opt) => (
            <Button schema={opt.schema} onClick={() => opt.onClick()}>
              {opt.text}
            </Button>
          )}
        </For>
      </div>
    )
  })

  const close = () => {
    props.onClose?.()
    props.close()
  }

  return (
    <Modal
      show={!!props.url}
      close={close}
      maxWidth="half"
      footer={
        <>
          <Button schema="secondary" onClick={close}>
            Close
          </Button>
          {footer()}
        </>
      }
    >
      <div class="flex justify-center p-4">
        <img class="rounded-md" src={getAssetUrl(props.url)} />
      </div>
    </Modal>
  )
}

const ImageCollectionModal: Component<{}> = (props) => {
  const [context] = useAppContext()
  const [imgctx] = useImageContext()
  const [presets] = usePresetContext()

  const reel = useImageCache()
  const nonChatPrompt = useDraft('image-prompt-global')

  const store = imageStore((s) => {
    return {
      src: s.showImage?.src,
      options: s.showImage?.options,
      onClose: s.showImage?.onClose,
      preview: s.preview,
    }
  })

  const collectionId = createMemo(() => {
    const id = store.src?.id
    if (!id) return ''
    return id
  })

  const show = createMemo(() => {
    const next = store.src?.id !== undefined && store.src.type !== 'url'
    return next
  })

  const imageSettings = useCurrentChatImageSettings()

  const [state, update] = createStore<ImageState>({
    loading: false,
    prompt: '',
    promptLoading: false,
  })

  const title = createMemo(() => {
    if (reel.state.images.length) {
      return `Image: ${reel.state.pos + 1}/${reel.state.images.length}`
    }

    return 'Image: 0/0'
  })

  const getSchemaPrompt = () => {
    if (!store.src?.messageId) return ''

    const msg = context.chatTree[store.src?.messageId]
    if (!msg) return

    const autoDefs = getJsonSchema({
      characterId: msg.msg.characterId,
      preset: presets.current,
    })

    if (!autoDefs?.schema) return ''

    const reply = msg.msg.characterId
      ? context.allBots[msg.msg.characterId]?.name
      : msg.msg.name || ''

    const rendered = hydrateTemplate(autoDefs.schema, msg.msg.json?.values, {
      replyAs: { name: reply },
      char: { name: context.char?.name || '' },
      impersonate: { name: context.impersonate?.name || '' },
      sender: context.profile,
    })

    return rendered.imageCaption || ''
  }

  const attachImage = () => {
    const chatId = imageSettings().chatId

    const btns: ConfirmAction[] = []

    if (chatId) {
      btns.push({
        text: 'To Unsent',
        schema: 'primary',
        onClick: () => msgStore.addAttachment(chatId, [{ type: 'image', image: reel.state.image }]),
      })
    }

    if (store.src?.messageId) {
      btns.push({
        text: 'To Current',
        schema: 'primary',
        onClick: () =>
          msgStore.addAttachment(store.src?.messageId!, [
            { type: 'image', image: reel.state.image },
          ]),
      })
    }

    pageStore.openConfirm({ message: 'Add Message Attachment', actions: btns })
  }

  createEffect(
    on(
      () => state.prompt,
      () => {
        if (store.src?.type !== 'collection' || !collectionId()) return
        console.log('global-prompt', state.prompt)
        nonChatPrompt.update(state.prompt)
      }
    )
  )

  createEffect(
    on(
      () => store.src?.id,
      () => {
        log('loading #%s %s ', store.src?.initial || 0, collectionId())
        reel.load(collectionId(), store.src?.initial)

        if (store.src?.type === 'message') {
          const msg = getGraphMessage(store.src.messageId)

          if (!msg?.imagePrompt) {
            const schemaPrompt = getSchemaPrompt()
            update('prompt', schemaPrompt || '')
            return
          }

          update('prompt', msg?.imagePrompt || '')
          return
        }

        if (store.src?.type === 'collection' && collectionId()) {
          const prompt = nonChatPrompt.open(`image-prompt-${collectionId()}`)
          update('prompt', prompt)
          return
        }

        update('prompt', store.src?.prompt || '')
      }
    )
  )

  const close = () => {
    store.onClose?.()
    imageStore.clearImage()
  }

  const removeImage = async () => {
    await reel.removeImage(reel.state.imageId)

    if (store.src?.messageId) {
      hydrateMessageImages(store.src.messageId)
    }
  }

  const generateImage = async () => {
    if (state.loading) return

    const imagePrompt = state.prompt

    update('loading', true)

    try {
      // If the source is a message, use the standardized message flow
      if (store.src?.messageId) {
        await msgStore.createImage({
          prompt: imagePrompt,
          sourceMsgId: store.src.messageId,
          append: true,
          onImage: (image) => reel.reload(),
          onPrompt: (prompt) => update('prompt', prompt),
          onTick: (text, state) => {
            if (state !== 'partial' && state !== 'done') return
            update('prompt', text)
          },
        })
      } else {
        const result = await imageApi.generateImageAsync(imagePrompt, {
          model: '',
          noAffix: false,
        })
        await reel.addImage(result.image, {
          id: result.file.name,
          prompt: imagePrompt,
        })
      }
    } catch (ex: any) {
      toastStore.error(`Image Generation Error: ${ex.message || ex}`)
      console.error(ex)
      update('loading', false)
    } finally {
      update('loading', false)
    }
  }

  const ImageNavigate = (
    <div class="flex items-center justify-center gap-2">
      <Button size="sm" disabled={reel.state.images.length <= 1} onClick={reel.prev}>
        <ArrowLeft size={20} />
      </Button>

      <Button
        size="sm"
        schema="primary"
        disabled={!reel.state.image}
        onClick={() => downloadImage({ name: reel.state.imageId, image: reel.state.image })}
      >
        <Download size={20} />
      </Button>

      <Show when={store.src?.messageId}>
        <Button
          size="sm"
          schema="primary"
          disabled={!reel.state.image || !store.src?.messageId}
          onClick={attachImage}
        >
          Attach to Message
        </Button>
      </Show>

      <Button size="sm" schema="error" onClick={removeImage} disabled={!reel.state.images.length}>
        <Trash size={20} />
      </Button>

      <Button size="sm" disabled={reel.state.images.length <= 1} onClick={reel.next}>
        <ArrowRight size={20} />
      </Button>
    </div>
  )

  const GenerationActions = (
    <div class="flex flex-col gap-2">
      <div class="flex w-full items-end justify-end">
        <SelectImageProvider ctx={imgctx} />
      </div>

      <div class="flex w-full items-end justify-end gap-2">
        <Button size="sm" onClick={generateImage} disabled={state.loading}>
          Generate Image
        </Button>

        <For each={store?.options || []}>
          {(action) => (
            <Button
              size="sm"
              schema={action.schema}
              onClick={() => action.onClick({ prompt: state.prompt, reel })}
            >
              {action.text}
            </Button>
          )}
        </For>
      </div>

      <div class="flex w-full justify-center">
        <Show when={store.preview}>
          <img src={store.preview?.base64} />
        </Show>
      </div>
    </div>
  )

  return (
    <Modal
      debug="img-col"
      show={show()}
      close={close}
      maxWidth="full"
      fixedHeight
      title={
        <div class="flex items-center gap-2">
          <div class="icon-button" onClick={() => getStore('image').imageSettings(true)}>
            <SettingsIcon size={20} />
          </div>
          <div>{title()}</div>
        </div>
      }
    >
      <PromptSettings
        state={state}
        update={update}
        messageId={store.src?.messageId}
        actions={GenerationActions}
        footer={ImageNavigate}
        settings={imageSettings()}
      >
        <section class="relative flex max-h-[calc(100%-200px)] justify-center">
          <Show when={state.loading}>
            <div class="bg-900 absolute right-1/2 top-1/2 rounded-lg p-2">
              <RelativeSpinner />
            </div>
          </Show>
          <Show when={!!reel.state.image}>
            <img class="min-h-0 rounded-sm object-contain" src={reel.state.image} />
          </Show>
        </section>
      </PromptSettings>
    </Modal>
  )
}

const PromptSettings: Component<{
  state: ImageState
  update: SetStoreFunction<ImageState>
  settings?: ChatImageSettings
  messageId: string | undefined
  children: JSX.Element
  actions: JSX.Element
  footer: JSX.Element
}> = (props) => {
  const persist = promptStore((s) => ({ imageHint: s.imageHint }))
  const msgs = msgStore((s) => ({ message: s.graph.tree[props.messageId || ''] }))

  const gen = genApi.inferenceSignal({
    onTick: (res, state) => {
      if (state === 'partial') props.update('prompt', res)
      if (state === 'done' || state === 'error') props.update('promptLoading', false)
    },
  })

  const fullImagePrompt = createMemo(() => {
    const parts = [props.settings?.prefix, props.state.prompt, props.settings?.suffix]
      .filter((c) => !!c?.trim())
      .join(', ')
    const cleaned = cleanPrompt(parts)
    return cleaned
  })

  const generatePrompt = async () => {
    if (gen.state.status === 'loading') {
      gen.cancel()
      return
    }

    const ents = await getImagePromptEntities(props.messageId)

    const template = imageApi.getSummaryTemplate({
      task: ents.summary,
      generate: 'Image Caption',
      focus: persist.imageHint,
    })

    await gen.send({ prompt: template, preset: ents.preset })
  }

  const onCleanPrompt = () => {
    const cleaned = cleanPrompt(props.state.prompt)
    props.update('prompt', cleaned)
  }

  const saveMessagePrompt = () => {
    if (!props.messageId) return
    msgStore.editMessageProp(props.messageId, { imagePrompt: props.state.prompt })
  }

  const canSave = createMemo(() => {
    if (!props.messageId) return false
    if (!msgs.message) return false

    if (msgs.message.msg.imagePrompt === fullImagePrompt()) return false
    return true
  })

  onMount(() => {
    promptStore.loadImageHint(msgs.message.msg.chatId)
  })

  return (
    <div class="image-modal">
      <section class="flex flex-col gap-1" style={{ 'grid-area': 'options' }}>
        <Show when={props.messageId}>
          <TextInput
            placeholder="Caption Hint: What to focus on when generating the caption?"
            class="!text-sm"
            onChange={(ev) =>
              promptStore.imageHint({
                chatId: msgs.message.msg.chatId,
                text: ev.currentTarget.value,
              })
            }
            value={persist.imageHint}
          />
        </Show>

        <TextInput
          parentClass="w-full sm:h-[200px] h-[80px] sm:max-h-[200px] max-h-[80px]"
          class="!h-full !py-1 !text-sm"
          prelabel="Prompt"
          value={props.state.prompt}
          onChange={(ev) => props.update('prompt', ev.currentTarget.value)}
          isMultiline
          textarea={{ rows: 3 }}
        />

        <div class="flex w-full items-center justify-between">
          <Copy text={fullImagePrompt()}>
            <span class="text-sm">Copy</span>
          </Copy>

          <div class="flex-end flex gap-2">
            <Show when={props.messageId}>
              <Button size="sm" onClick={generatePrompt}>
                <Show
                  when={gen.state.status !== 'loading'}
                  fallback={
                    <>
                      <RelativeSpinner size={20} />
                      Stop
                    </>
                  }
                >
                  <WandSparkles size={16} /> Prompt
                </Show>
              </Button>
              <Button size="sm" onClick={onCleanPrompt}>
                Fix
              </Button>

              <Show when={props.messageId}>
                <Button size="sm" onClick={saveMessagePrompt} disabled={!canSave()}>
                  Save
                </Button>
              </Show>
            </Show>
          </div>
        </div>

        {props.actions}
      </section>

      <section class="image-modal-body" style={{ 'grid-area': 'content' }}>
        {props.children}
      </section>

      <section class="flex justify-center gap-2" style={{ 'grid-area': 'footer' }}>
        {props.footer}
      </section>
    </div>
  )
}

function getGraphMessage(id: string | undefined) {
  if (!id) return
  const { graph } = msgStore.getState()
  const msg = graph.tree[id]
  return msg?.msg
}
